import { logger } from "@/logger";
import { ensureValidAccessToken, refreshAccessToken } from "@/api/HttpClient";
import type { AddUploadInput } from "@/features/upload/upload-worker/types";
import { map } from "rxjs/operators";

/** Re-export worker task type for callers. Use AddUploadInput for enqueue. */
export type { AddUploadInput as UploadWorkerEnqueueTask };

export interface UploadWorkerItemSnapshot {
  id: string;
  fileName: string;
  folderId?: string;
  relativePath?: string;
  size: number;
  progress: number;
  status: "pending" | "uploading" | "retrying" | "completed" | "failed" | "paused" | "cancelled";
  retryCount: number;
  maxRetries: number;
  error?: string;
  uploadedBytes: number;
  updatedAt: number;
  /** Set when completed (from backend confirm). */
  fileId?: string;
  versionResult?: unknown;
  isOrganization?: boolean;
}

export interface UploadWorkerSnapshot {
  maxConcurrentUploads: number;
  isPaused: boolean;
  counts: {
    total: number;
    pending: number;
    uploading: number;
    completed: number;
    failed: number;
    paused: number;
    cancelled: number;
    retrying: number;
  };
  items: UploadWorkerItemSnapshot[];
}

export interface UploadWorkerEvent {
  event:
    | "ITEM_UPDATED"
    | "STATE_CHANGED"
    | "QUEUE_DRAINED"
    | "AUTH_EXPIRED"
    | "UPLOAD_FAILED"
    | "progress"
    | "retrying"
    | "paused"
    | "resumed"
    | "completed"
    | "failed"
    | "cancelled"
    | string;
  item?: UploadWorkerItemSnapshot;
  result?: any; // For "completed" event, contains backend response data from confirm API
  snapshot?: UploadWorkerSnapshot;
  uploadRecord: {
            fileId: string;
            backendFileId: string;
            name: string;
            size: number;
            type: string; // MIME type would need to be stored separately
            companyId: string;
            project: string;
            parentId: string;
            folderId: string;
            relativePath: string;
            isOrg: boolean;
            objectKey: string;
            cloudLink: string;
            version: string;
            mimeType: string;
            bucketName: string;
            id: string; // document ID from item
  };
  /** User-visible message when event is UPLOAD_FAILED (e.g. "Upload failed: filename - reason"). */
  message?: string;
}

type PendingRequest = {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  timeout: ReturnType<typeof setTimeout>;
};

function safeErrorMessage(input: unknown): string {
  if (typeof input === "string") return input;
  if (input instanceof Error) return input.message || "Unknown service worker error";
  if (input && typeof input === "object") return "Service worker request failed";
  if (typeof input === "number" || typeof input === "boolean" || input == null) return String(input ?? "Unknown service worker error");
  return "Service worker request failed";
}

export class UploadServiceWorkerClient {
  private scriptUrl: string;
  private registration: ServiceWorkerRegistration | null = null;
  private requests = new Map<string, PendingRequest>();
  private listeners = new Set<(event: UploadWorkerEvent) => void>();
  private isRegistered = false;
  private refreshInFlight = false;
  /** Worker task id → File, for SW conflict modal (same File reference as enqueue). */
  private enqueuedTaskFiles = new Map<string, File>();

  constructor(scriptUrl = "/upload-service-worker.js") {
    this.scriptUrl = scriptUrl;
    if (typeof window !== "undefined") {
      navigator.serviceWorker?.addEventListener("message", this.onMessage);
      window.addEventListener("online", this.onOnline);
    }
  }

  /** Notify SW to resume queue when network reconnects. SW's own 'online' event is unreliable. */
  private onOnline = (): void => {
    logger.debug("[UploadSWClient] network online - requesting queue resume");
    this.resumeQueue().catch((err) => logger.warn("[UploadSWClient] resume on online failed", err));
  };

  private async resolveAccessToken(forceRefresh = false): Promise<string | null> {
    return new Promise((resolve, reject) => {
      const source$ = forceRefresh
        ? refreshAccessToken().pipe(map((res) => res?.accessToken ?? null))
        : ensureValidAccessToken();
      source$.subscribe({
        next: (value) => resolve(value),
        error: reject,
        complete: () => resolve(null),
      });
    });
  }

  private async syncAuthHeaders(forceRefresh = false): Promise<void> {
    const accessToken = await this.resolveAccessToken(forceRefresh);
    if (!accessToken) return;
    await this.updateAuthHeaders({ Authorization: `Bearer ${accessToken}` });
  }

  /** Wait for this registration to have an active worker (so we can postMessage to our upload SW). */
  private async waitForActiveWorker(): Promise<void> {
    const reg = this.registration;
    if (!reg || reg.active) return;
    const sw = reg.installing || reg.waiting;
    if (!sw) return;
    return new Promise<void>((resolve) => {
      const onStateChange = (): void => {
        if (reg.active) {
          sw.removeEventListener("statechange", onStateChange);
          resolve();
        }
      };
      sw.addEventListener("statechange", onStateChange);
      if (reg.active) resolve();
    });
  }

  /** Ensure the upload service worker is registered and ready (idempotent). Reuses registration from main.tsx when present. */
  async register(): Promise<void> {
    if (this.isRegistered) return;
    if (!("serviceWorker" in navigator)) {
      throw new Error("Service Worker is not supported in this browser.");
    }

    const existing = await navigator.serviceWorker.getRegistration(this.scriptUrl);
    if (existing) {
      logger.debug("[UploadSWClient] reusing existing registration", { scriptUrl: this.scriptUrl });
      this.registration = existing;
      await existing.update().catch((err) => {
        logger.warn("[UploadSWClient] service worker update check failed", err);
      });
    } else {
      logger.debug("[UploadSWClient] register", { scriptUrl: this.scriptUrl });
      this.registration = await navigator.serviceWorker.register(this.scriptUrl, { updateViaCache: "none" });
    }
    await this.waitForActiveWorker();
    await navigator.serviceWorker.ready;
    this.isRegistered = true;
    logger.debug("[UploadSWClient] ready", { hasActive: !!this.registration?.active });
  }

  /** Remove listeners and reject in-flight requests when client is disposed. */
  destroy(): void {
    if (typeof window !== "undefined") {
      navigator.serviceWorker?.removeEventListener("message", this.onMessage);
      window.removeEventListener("online", this.onOnline);
    }

    for (const pending of this.requests.values()) {
      clearTimeout(pending.timeout);
      pending.reject(new Error("UploadServiceWorkerClient has been destroyed."));
    }

    this.requests.clear();
    this.listeners.clear();
  }

  /** Subscribe to service-worker upload events and return an unsubscribe function. */
  subscribe(listener: (event: UploadWorkerEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Configure worker queue behavior (global endpoint, concurrency, etc.). */
  async configure(options: {
    endpoint?: string;
    maxConcurrentUploads?: number;
    maxConcurrentParts?: number;
    defaultChunkSizeBytes?: number;
    defaultMaxRetries?: number;
    debug?: boolean;
  }): Promise<void> {
    logger.debug("[UploadSWClient] configure", options);
    await this.request("CONFIGURE", options);
  }

  /** Enqueue one or more uploads into the worker queue and return worker ids. */
  async enqueue(tasks: AddUploadInput[]): Promise<string[]> {
    logger.debug("[UploadSWClient] enqueue", { count: tasks.length });
    await this.syncAuthHeaders(true).catch(async (err) => {
      logger.warn("[UploadSWClient] pre-enqueue forced auth sync failed", err);
      await this.syncAuthHeaders(false).catch((fallbackErr) => {
        logger.warn("[UploadSWClient] pre-enqueue auth sync fallback failed", fallbackErr);
      });
    });
    for (const task of tasks) {
      const id = task.id;
      if (id && task.file) this.enqueuedTaskFiles.set(id, task.file);
    }
    const response = (await this.request("ENQUEUE", { tasks })) as { ids?: string[] } | undefined;
    const ids = Array.isArray(response?.ids) ? response.ids : [];
    logger.debug("[UploadSWClient] enqueue done", { requested: tasks.length, ids: ids.length });
    return ids;
  }

  /**
   * Notify the service worker UploadManager that the user resolved a file-exists conflict
   * (same modal as checkFileExistsEpic flow).
   */
  async notifyConflictResolved(
    requestId: string,
    resolution: { action: string; version?: string; existingFileId?: number }
  ): Promise<void> {
    await this.request("CONFLICT_RESOLUTION", {
      requestId,
      action: resolution.action,
      version: resolution.version,
      existingFileId: resolution.existingFileId,
    });
  }

  /** Pause dispatching new queue items in worker. */
  async pauseQueue(): Promise<void> {
    await this.request("PAUSE_QUEUE");
  }

  /** Pause queue dispatch and cancel every active upload in the worker. */
  async stopAll(): Promise<void> {
    await this.request("STOP_ALL");
  }

  /** Resume queue dispatching in worker. */
  async resumeQueue(): Promise<void> {
    await this.request("RESUME_QUEUE");
  }

  /** Pause one upload item by id. */
  async pauseItem(itemId: string): Promise<void> {
    await this.request("PAUSE_ITEM", { itemId });
  }

  /** Resume one paused/failed upload item by id. */
  async resumeItem(itemId: string): Promise<void> {
    await this.request("RESUME_ITEM", { itemId });
  }

  /** Requeue one failed/paused upload item for retry. */
  async retryItem(itemId: string): Promise<void> {
    await this.request("RETRY_ITEM", { itemId });
  }

  /** Cancel one upload item by id. */
  async cancelItem(itemId: string): Promise<void> {
    await this.request("CANCEL_ITEM", { itemId });
  }

  /** Request current queue snapshot from worker. */
  async snapshot(): Promise<UploadWorkerSnapshot | null> {
    const response = (await this.request("SNAPSHOT")) as { snapshot?: UploadWorkerSnapshot | null } | undefined;
    return response?.snapshot ?? null;
  }

  /** Clear non-active upload history entries persisted by the worker. */
  async clearHistory(): Promise<UploadWorkerSnapshot | null> {
    const response = (await this.request("CLEAR_HISTORY")) as { snapshot?: UploadWorkerSnapshot | null } | undefined;
    return response?.snapshot ?? null;
  }

  /**
   * Push fresh auth headers to the worker (e.g. after token refresh). Used when the worker sends REQUEST_REFRESH_TOKEN on 401.
   */
  async updateAuthHeaders(headers: Record<string, string>): Promise<void> {
    await this.request("UPDATE_AUTH_HEADERS", { headers });
  }

  private buildEventDebugInfo(payload: UploadWorkerEvent & Record<string, unknown>): Record<string, unknown> {
    const snapshot = payload.snapshot as UploadWorkerSnapshot | undefined;
    const counts = snapshot?.counts;
    const rawFileId = payload.fileId;
    const fileId = typeof rawFileId === "string" ? rawFileId : undefined;
    const isGlobalProgress = payload.event === "progress" && fileId === "__global__";
    const item = fileId && snapshot?.items ? snapshot.items.find((i) => i.id === fileId) : undefined;
    const uploadedBytes = typeof payload.uploadedBytes === "number" ? payload.uploadedBytes : item?.uploadedBytes;
    const totalBytes = typeof payload.totalBytes === "number" ? payload.totalBytes : item?.size;
    const progressPercent =
      typeof uploadedBytes === "number" && typeof totalBytes === "number" && totalBytes > 0
        ? Number(((uploadedBytes / totalBytes) * 100).toFixed(2))
        : item
          ? Number((item.progress * 100).toFixed(2))
          : undefined;

    const rawChunkIndex = payload.chunkIndex;
    const chunkIndexFromField =
      typeof rawChunkIndex === "number"
        ? rawChunkIndex
        : (typeof rawChunkIndex === "string" && rawChunkIndex.trim() !== "" && Number.isFinite(Number(rawChunkIndex)))
          ? Number(rawChunkIndex)
          : undefined;
    const taskId = typeof payload.taskId === "string" ? payload.taskId : undefined;
    const chunkIndexFromTaskId = (() => {
      if (!taskId) return undefined;
      const match = /_chunk_(\d+)$/.exec(taskId);
      if (!match) return undefined;
      const n = Number(match[1]);
      return Number.isFinite(n) ? n : undefined;
    })();
    const chunkIndex = chunkIndexFromField ?? chunkIndexFromTaskId;

    const base = {
      event: payload.event,
      scope: isGlobalProgress ? "global" : "file",
      fileId,
      uploadedBytes,
      totalBytes,
      progressPercent,
      message: typeof payload.message === "string" ? payload.message : undefined,
      error: typeof payload.error === "string" ? payload.error : undefined,
      queue: counts
        ? {
          total: counts.total,
          uploading: counts.uploading,
          retrying: counts.retrying,
          completed: counts.completed,
          failed: counts.failed,
          paused: counts.paused,
        }
        : undefined,
    };
    if (isGlobalProgress) return base;
    return {
      ...base,
      fileName: typeof payload.fileName === "string" ? payload.fileName : item?.fileName,
      status: item?.status,
      retryCount: typeof payload.retryCount === "number" ? payload.retryCount : item?.retryCount,
      taskId,
      chunkIndex,
    };
  }

  /** Route worker responses/events to pending promises and subscribers. */
  private onMessage = (event: MessageEvent): void => {
    try {
      const source = event.source as ServiceWorker | null;
      if (source?.scriptURL && !source.scriptURL.endsWith(this.scriptUrl)) return;

      const data = event.data;
      if (!data || typeof data !== "object") return;
      const type = typeof (data as { type?: unknown }).type === "string" ? (data as { type: string }).type : "";
      if (!type) return;

      if (type === "UPLOAD_SW_CONFLICT") {
        const d = data as {
          type: string;
          requestId?: string;
          itemId?: string;
          fileName?: string;
          projectId?: string;
          parentId?: string;
          companyId?: string;
          isOrganization?: boolean;
          existingFileId?: number;
          version?: string;
          currentVersion?: number;
        };
        const requestId = typeof d.requestId === "string" ? d.requestId : "";
        const itemId = typeof d.itemId === "string" ? d.itemId : "";
        const file = itemId ? this.enqueuedTaskFiles.get(itemId) : undefined;
        if (!requestId || !file) {
          logger.warn("[UploadSWClient] UPLOAD_SW_CONFLICT missing requestId or file", { requestId, itemId });
          if (requestId) {
            void this.notifyConflictResolved(requestId, { action: "cancel" }).catch(() => null);
          }
          return;
        }
        const rawExId = d.existingFileId;
        const existingFileId =
          typeof rawExId === "number" && Number.isFinite(rawExId)
            ? rawExId
            : typeof rawExId === "string"
              ? Number.parseInt(rawExId, 10)
              : NaN;
        const safeExistingFileId = Number.isFinite(existingFileId) ? existingFileId : -1;
        void (async (): Promise<void> => {
          try {
            const { store } = await import("@/store");
            const { uploadFileActions } = await import("@/features/upload/stores/uploadFiles/uploadFileSlice");
            store.dispatch(
              uploadFileActions.enqueueConflict({
                files: [file],
                project: String(d.projectId ?? ""),
                existingFiles: [
                  {
                    file,
                    fileId: safeExistingFileId,
                    version: d.version,
                    currentVersion: d.currentVersion,
                  },
                ],
                meta: {
                  parentFolderId: d.parentId,
                  parentFolderIdMap: {},
                  companyId: String(d.companyId ?? ""),
                  teamId: undefined,
                  isOrganization: Boolean(d.isOrganization),
                  swConflictRequestId: requestId,
                  swWorkerItemId: itemId,
                  uniqueIds: [itemId],
                },
              })
            );
            store.dispatch(uploadFileActions.processNextConflict());
          } catch (err) {
            logger.warn("[UploadSWClient] UPLOAD_SW_CONFLICT dispatch failed", err);
            void this.notifyConflictResolved(requestId, { action: "cancel" }).catch(() => null);
          }
        })();
        return;
      }

      if (type === "REQUEST_REFRESH_TOKEN") {
        if (this.refreshInFlight) return;
        this.refreshInFlight = true;
        logger.debug("[UploadSWClient] worker requested refresh token");
        refreshAccessToken().subscribe({
          next: (response) => {
            const accessToken = response?.accessToken;
            if (accessToken) {
              this.updateAuthHeaders({ Authorization: `Bearer ${accessToken}` }).catch((err) => {
                logger.warn("[UploadSWClient] updateAuthHeaders after auth check failed", err);
              });
            }
            this.refreshInFlight = false;
          },
          error: (err) => {
            logger.warn("[UploadSWClient] forced refresh token failed", err);
            ensureValidAccessToken().subscribe({
              next: (accessToken) => {
                if (accessToken) {
                  this.updateAuthHeaders({ Authorization: `Bearer ${accessToken}` }).catch((updateErr) => {
                    logger.warn("[UploadSWClient] updateAuthHeaders after auth fallback failed", updateErr);
                  });
                }
                this.refreshInFlight = false;
              },
              error: (fallbackErr) => {
                logger.warn("[UploadSWClient] refresh fallback failed", fallbackErr);
                this.refreshInFlight = false;
              },
              complete: () => {
                this.refreshInFlight = false;
              },
            });
          },
          complete: () => {
            this.refreshInFlight = false;
          }
        });
        return;
      }

      if (type === "UPLOAD_SW_RESPONSE" && (data as { requestId?: unknown }).requestId) {
        const response = data as { requestId: string; ok?: boolean; data?: unknown; error?: unknown };
        if (typeof response.requestId !== "string") return;
        const request = this.requests.get(response.requestId);
        if (!request) return;

        this.requests.delete(response.requestId);
        clearTimeout(request.timeout);

        if (response.ok) {
          request.resolve(response.data);
        } else {
          request.reject(safeErrorMessage(response.error));
        }
        return;
      }

      if (type === "UPLOAD_SW_EVENT") {
        const rawPayload = (data as { payload?: unknown }).payload;
        if (!rawPayload || typeof rawPayload !== "object") return;
        const payload = rawPayload as UploadWorkerEvent & Record<string, unknown>;
        const eventType = payload?.event;
        const debugInfo = this.buildEventDebugInfo(payload);
        if (eventType === "failed") {
          logger.warn("[UploadSWClient] event", debugInfo);
        } else {
          logger.debug("[UploadSWClient] event", debugInfo);
        }
        for (const listener of this.listeners) {
          try {
            listener(payload);
          } catch (err) {
            logger.warn("[UploadSWClient] event listener failed", err);
          }
        }
      }
    } catch (err) {
      logger.warn("[UploadSWClient] onMessage handler failed", err);
    }
  };

  /** Send one command to worker and await correlated response payload. */
  private async request(type: string, payload?: unknown): Promise<unknown> {
    await this.register();

    // Prefer our registration's active worker so messages always go to the upload SW (not another controller).
    const controller =
      this.registration?.active ||
      (navigator.serviceWorker.controller?.scriptURL.endsWith(this.scriptUrl) ? navigator.serviceWorker.controller : null) ||
      this.registration?.waiting ||
      this.registration?.installing ||
      null;
    if (!controller) throw new Error("No active service worker target found.");

    const requestId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    logger.debug("[UploadSWClient] request", { type, requestId, payload });
    return new Promise((resolve, reject) => {
      let settled = false;
      const channel = typeof MessageChannel !== "undefined" ? new MessageChannel() : null;
      const settleResolve = (value: unknown): void => {
        if (settled) return;
        settled = true;
        this.requests.delete(requestId);
        clearTimeout(timeout);
        if (channel) channel.port1.onmessage = null;
        resolve(value);
      };
      const settleReject = (reason: unknown): void => {
        if (settled) return;
        settled = true;
        this.requests.delete(requestId);
        clearTimeout(timeout);
        if (channel) channel.port1.onmessage = null;
        reject(reason);
      };
      const timeout = setTimeout(() => {
        settleReject(`Service worker request timed out: ${type}`);
      }, 15000);

      this.requests.set(requestId, {
        resolve: settleResolve,
        reject: settleReject,
        timeout,
      });

      if (channel) {
        channel.port1.onmessage = (event: MessageEvent): void => {
          const data = event.data as { type?: string; requestId?: string; ok?: boolean; data?: unknown; error?: unknown } | undefined;
          if (!data || data.type !== "UPLOAD_SW_RESPONSE" || data.requestId !== requestId) return;
          if (data.ok) {
            settleResolve(data.data);
          } else {
            settleReject(safeErrorMessage(data.error));
          }
        };
        controller.postMessage({ type, payload, requestId }, [channel.port2]);
        return;
      }
      controller.postMessage({ type, payload, requestId });
    });
  }
}

export const uploadSwClient =
  typeof window !== "undefined" ? new UploadServiceWorkerClient("/upload-service-worker.js") : null;
